import React, { useCallback, useEffect, useRef, useState } from 'react';

import { bool, node, string, number, func, oneOfType } from 'prop-types';
import PrismaZoom from 'react-prismazoom';
import styled from 'styled-components';

const ZoomContainer = styled(PrismaZoom)`
  height: 100%;
`;

const Wrapper = styled.div``;

function ImageZoom({
  animationDuration = 0.05, // (in seconds)
  children,
  className,
  isHoverEnabled = false,
  isPanEnabled = true,
  isZoomEnabled = true,
  maxZoom = 2,
  minZoom = 1,
  onZoomChange = () => {},
  scrollVelocity = 3,
  zoomKey,
}) {
  const [isZoomed, setIsZoomed] = useState(false);
  const zoomRef = useRef();

  const resetZoom = () => {
    if (zoomRef.current) {
      zoomRef.current.reset();
    }
  };

  const handleMouseEnter = useCallback(
    event => {
      if (!isZoomed && zoomRef.current) {
        zoomRef.current.fullZoomInOnPosition(event.pageX, event.pageY);
      }
    },
    [isZoomed]
  );

  const handleMouseMove = useCallback(
    event => {
      if (isZoomed && zoomRef.current) {
        const zoomRatio = maxZoom > 2 ? maxZoom - 1 : 1;
        const [movementX, movementY] = [
          event.movementX * -1 * zoomRatio,
          event.movementY * -1 * zoomRatio,
        ];
        zoomRef.current.move(movementX, movementY, animationDuration);
      }
    },
    [animationDuration, isZoomed, maxZoom]
  );

  const handleMouseLeave = useCallback(() => resetZoom(), []);

  const handleZoomChange = zoomValue => {
    const _isZoomed = zoomValue !== minZoom;
    setIsZoomed(_isZoomed);

    if (onZoomChange) {
      onZoomChange(_isZoomed);
    }
  };

  useEffect(() => {
    /*
     * Passing in a new zoomKey will zoom out the image
     */
    resetZoom();
  }, [zoomKey]);

  /*
   * Since react-prismazoom breaks on SSR, we'll render the children props instead
   * without the zoom functionality
   */
  if (typeof window === 'undefined') {
    return children;
  }

  return (
    <Wrapper
      className={className}
      onMouseEnter={isHoverEnabled ? handleMouseEnter : null}
      onMouseLeave={isHoverEnabled ? handleMouseLeave : null}
      onMouseMove={isHoverEnabled ? handleMouseMove : null}
    >
      <ZoomContainer
        allowPan={isPanEnabled}
        allowZoom={isZoomEnabled}
        animDuration={animationDuration}
        maxZoom={maxZoom}
        minZoom={minZoom}
        onZoomChange={handleZoomChange}
        ref={zoomRef}
        scrollVelocity={scrollVelocity}
      >
        {children}
      </ZoomContainer>
    </Wrapper>
  );
}

ImageZoom.propTypes = {
  animationDuration: number,
  children: node,
  className: string,
  isHoverEnabled: bool,
  isPanEnabled: bool,
  isZoomEnabled: bool,
  maxZoom: number,
  minZoom: number,
  onZoomChange: func,
  scrollVelocity: number,
  zoomKey: oneOfType([string, number]),
};

export default ImageZoom;
